import React, { useState, useEffect } from 'react';
import './AddSupplierModal.css';

const AddSupplierModal = ({ open, onClose, onSupplierAdded, editSupplier = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    contactInfo: '',
    email: '',
    phone: '',
    address: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Reset form when modal opens/closes or when editSupplier changes
  useEffect(() => {
    if (open) {
      if (editSupplier) {
        setFormData({
          name: editSupplier.name || '',
          contactInfo: editSupplier.contactInfo || '',
          email: editSupplier.email || '',
          phone: editSupplier.phone || '',
          address: editSupplier.address || ''
        });
      } else {
        setFormData({
          name: '',
          contactInfo: '',
          email: '',
          phone: '',
          address: ''
        });
      }
      setError('');
    }
  }, [open, editSupplier]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      setError('Supplier name is required');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const url = editSupplier 
        ? `${process.env.REACT_APP_API_URL || ''}/suppliers/${editSupplier.id}`
        : `${process.env.REACT_APP_API_URL || ''}/suppliers`;
      
      const method = editSupplier ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save supplier');
      }

      const supplier = await response.json();
      
      // Call the callback to refresh the suppliers list
      if (onSupplierAdded) {
        onSupplierAdded(supplier);
      }
      
      // Close the modal
      onClose();
      
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content supplier-modal">
        <div className="modal-header">
          <h2>{editSupplier ? '✏️ Edit Supplier' : '🏢 Add New Supplier'}</h2>
          <button 
            className="modal-close" 
            onClick={handleClose}
            disabled={loading}
          >
            &times;
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-section">
            <h3>Basic Information</h3>
            <div className="form-group">
              <label htmlFor="name">
                Supplier Name <span className="required">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Enter supplier name"
                required
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label htmlFor="contactInfo">General Contact Information</label>
              <textarea
                id="contactInfo"
                name="contactInfo"
                value={formData.contactInfo}
                onChange={handleInputChange}
                placeholder="Enter general contact information, notes, or instructions"
                rows="3"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-section">
            <h3>Contact Details</h3>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">📧 Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="supplier@example.com"
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label htmlFor="phone">📞 Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+27 11 123 4567"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="address">📍 Physical Address</label>
              <textarea
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                placeholder="Enter complete physical address for deliveries"
                rows="3"
                disabled={loading}
              />
            </div>
          </div>

          {error && (
            <div className="error-message">
              <strong>Error:</strong> {error}
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              onClick={handleClose}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="loading-spinner"></span>
                  Saving...
                </>
              ) : (
                editSupplier ? '💾 Update Supplier' : '➕ Add Supplier'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddSupplierModal;
